namespace SaNavigationUtils {
    /** Utility class for determining the current Node.js environment. */
    export class NodeEnv {
        public static isDevelopment(): boolean {
            return process.env.NODE_ENV === "development";
        }

        public static isProduction(): boolean {
            return process.env.NODE_ENV === "production";
        }

        public static isTest(): boolean {
            return process.env.NODE_ENV === "test";
        }
    }

    export const PROTOCOL_REGEX = /^https?:\/\//i;

    const PROD_BASE_URL = "https://www.scrumalliance.org";

    export enum ChangeUrlOption {
        WithHistoryPageLoad,
        WithoutHistoryPageLoad,
        WithHistoryNoPageLoad,
        WithoutHistoryNoPageLoad,
    }

    // #region URL Manipulation

    /** Opinionated Current Base URL finder, returns the base URL depending on the environment. */
    export function getBaseUrl() {
        // For www, certification, and resources
        let baseurl = PROD_BASE_URL;
        // For stage-kentico, stage-certification, and stage-resources
        if (window.location.hostname.includes("stage")) {
            baseurl = "https://stage-kentico.scrumalliance.org";
        } else if (
            window.location.hostname == "dev.scrumalliance.org" ||
            window.location.hostname == "sf.scrumalliance.org" ||
            window.location.hostname == "localhost" ||
            window.location.hostname.includes("alpha")
        ) {
            baseurl = window.location.origin;
        }
        return baseurl;
    }

    /** Gets the base URL for the Rails application depending on the environment. */
    export function getRailsBaseUrl() {
        const baseUrl = getBaseUrl();
        if (baseUrl === PROD_BASE_URL) {
            return "https://certification.scrumalliance.org";
        }
        return "https://stage-certification.scrumalliance.org";
    }

    /**
     * Gets the current page URL as a URL object. This is useful to easily manipulate the URL and its parameters.
     */
    export function getCurrentPageUrl(): URL {
        return new URL(globalThis.location.href);
    }

    /**
     * Gets the parent path of the current page URL. For example, if the current URL is "https://www.scrumalliance.org/courses/edit/123", this function will return "https://www.scrumalliance.org/courses/edit". This is useful for navigating back to a listing page after performing an action on a details page, for example.
     */
    export function getParentPath() {
        const pathname = window.location.pathname;
        const pathSegments = pathname.split("/").filter(Boolean);

        if (pathSegments.length <= 1) {
            return window.location.origin;
        }

        const parentPath = `/${pathSegments.slice(0, -1).join("/")}`;

        return `${window.location.origin}${parentPath}`;
    }

    /**
     * Checks if the given URL has a protocol (http or https). This is useful for validating user input for URLs and ensuring that they are in a correct format before attempting to navigate to them.
     */
    export function hasProtocol(url: string): boolean {
        return PROTOCOL_REGEX.test(url);
    }

    /**
     * Ensures that the given URL has a protocol (http or https). If the URL already has a protocol, it is returned as-is. If it does not have a protocol, "https://" is prepended to the URL and returned. This is useful for ensuring that URLs are valid and can be navigated to without errors, especially when accepting user input for URLs.
     */
    export function ensureUrlHasProtocol(url: string): string {
        if (hasProtocol(url)) {
            return url;
        }

        return "https://" + url;
    }

    /**
     * Ensures the given URL has the current origin (protocol, domain, and port). If the URL already starts with a protocol, it is returned as-is. If it doesn't, the current origin is prepended.
     * @param url - URL to ensure has origin
     * @returns URL with origin prepended if needed
     */
    export function ensureUrlHasOrigin(url: string): string {
        if (hasProtocol(url)) {
            return url;
        }

        return globalThis.location.origin + url;
    }

    /**
     * Changes the current URL using the specified option.
     * @param url - The new URL to apply
     * @param option - How to handle the URL change (default: WithoutHistoryNoPageLoad)
     */
    export function changeUrl(
        url: string | URL,
        option: ChangeUrlOption = ChangeUrlOption.WithoutHistoryNoPageLoad,
    ): void {
        switch (option) {
            case ChangeUrlOption.WithHistoryPageLoad:
                navigateWithHistory(url);
                break;
            case ChangeUrlOption.WithoutHistoryPageLoad:
                navigateWithoutHistory(url);
                break;
            case ChangeUrlOption.WithHistoryNoPageLoad:
                updateUrlWithHistory(url);
                break;
            case ChangeUrlOption.WithoutHistoryNoPageLoad:
                updateUrlWithoutHistory(url);
                break;
        }
    }

    /**
     * Navigates to the given URL in the same tab in the normal way, which adds an entry to the browser history. Use navigateWithoutHistory if you don't want to add an entry to the browser history, or openInNewTab if you want to open in a new tab instead of the same tab.
     * @param url - URL to navigate to
     */
    export function navigateWithHistory(url: string | URL): void {
        const fullUrl = ensureUrlHasOrigin(url.toString());
        if (!isCurrentUrl(fullUrl)) {
            globalThis.location.assign(fullUrl);
        }
    }

    /**
     * Navigates to the given URL in the same tab but without adding an entry to the browser history, meaning the user won't be able to click "Back" to return to the previous page since it will be replaced in the history with the new page. Use navigateWithHistory if you want to add an entry to the browser history, or openInNewTab if you want to open in a new tab instead of the same tab.
     * @param url - URL to navigate to
     */
    export function navigateWithoutHistory(url: string | URL): void {
        const fullUrl = ensureUrlHasOrigin(url.toString());
        if (!isCurrentUrl(fullUrl)) {
            globalThis.location.replace(fullUrl);
        }
    }

    export function reload(): void {
        globalThis.location.reload();
    }

    /**
     * Updates browser to the given URL and adds to browser history without reloading the page. This is useful for updating the URL to reflect the current state of the application without causing a full page reload.
     * @param url - URL to update
     */
    export function updateUrlWithHistory(url: string | URL): void {
        const fullUrl = ensureUrlHasOrigin(url.toString());
        if (!isCurrentUrl(fullUrl)) {
            globalThis.history.pushState(null, "", fullUrl);
            notifyUrlChanged();
        }
    }

    /**
     * Updates browser to the given URL but does not add to browser history and does not reload the page. Use updateUrlWithoutHistory if you don't want to add to history and also not reload the page
     */
    export function updateUrlWithoutHistory(url: string | URL): void {
        const fullUrl = ensureUrlHasOrigin(url.toString());
        if (!isCurrentUrl(fullUrl)) {
            globalThis.history.replaceState(null, "", fullUrl);
            notifyUrlChanged();
        }
    }

    /** Use to fire an event whenever the URL changes without a page load. */
    export const UrlChangedEvent = "saUrlChanged";
    export function notifyUrlChanged(
        eventName: string = UrlChangedEvent,
    ): void {
        globalThis.dispatchEvent(new Event(eventName));
    }

    /**
     * Opens the given URL in a new browser tab. Use navigateWithHistory or navigateWithoutHistory if you want to navigate in the same tab instead of opening a new tab.
     */
    export function openInNewTab(url: string | URL): void {
        globalThis.open(url.toString(), "_blank", "noreferrer"); // Covers noopener too
    }

    /**
     * Returns only the path, query string, and hash of a URL, excluding the protocol and domain.
     * @param url - URL to extract path from
     * @returns Path portion of URL (e.g., "/courses/search?filter=1#section")
     */
    export function getPathFromUrl(url: string | URL): string {
        const urlObj = typeof url === "string" ? new URL(url) : url;
        return urlObj.pathname + urlObj.search + urlObj.hash;
    }

    /**
     * Checks if the given URL matches the current page URL.
     * @param url - URL to compare against current URL
     * @returns True if the URLs match, false otherwise
     */
    export function isCurrentUrl(url: string | URL): boolean {
        const fullUrl = ensureUrlHasOrigin(url.toString());
        const currentUrl = getCurrentPageUrl().toString();
        return fullUrl === currentUrl;
    }

    /**
     * Removes the specified URL parameters from the current page URL and navigates using the specified option.
     * @param paramNames - Array of parameter names to remove
     * @param option - How to navigate after removing parameters (default: WithoutHistoryNoPageLoad)
     */
    export function removeUrlParameters(
        paramNames: string[],
        option: ChangeUrlOption,
    ): void {
        const currentUrl = getCurrentPageUrl();
        paramNames.forEach((paramName) => {
            currentUrl.searchParams.delete(paramName);
        });

        const newUrl =
            currentUrl.pathname +
            (currentUrl.search ? currentUrl.search : "") +
            currentUrl.hash;
        changeUrl(newUrl, option);
    }

    /**
     * Adds or updates multiple URL parameters on the current page URL and navigates using the specified option.
     * @param params - Array of [paramName, paramValue] tuples
     * @param option - How to navigate after adding parameters (default: WithoutHistoryNoPageLoad)
     */
    export function addOrUpdateUrlParameters(
        params: Array<[string, string | number | boolean]>,
        option: ChangeUrlOption = ChangeUrlOption.WithoutHistoryNoPageLoad,
    ): void {
        const currentUrl = getCurrentPageUrl();
        params.forEach(([paramName, paramValue]) => {
            currentUrl.searchParams.set(paramName, String(paramValue));
        });

        changeUrl(currentUrl, option);
    }

    /**
     * Gets a URL parameter value from the current page URL.
     * @param paramName - Name of the parameter to get
     * @returns Parameter value or null if not found
     */
    export function getUrlParameter(paramName: string): string | null {
        const currentUrl = getCurrentPageUrl();
        return currentUrl.searchParams.get(paramName);
    }

    /**
     * Removes a single URL parameter from the current page URL and updates with history.
     * @param paramName - Name of the parameter to remove
     */
    export function removeUrlParameter(paramName: string): void {
        const currentUrl = getCurrentPageUrl();
        currentUrl.searchParams.delete(paramName);
        updateUrlWithHistory(currentUrl);
    }
    // #endregion

    // #region Scrolling

    /**
     * Found in the moreResultsScrollTopPartial.
     */
    export const ScrollToTopButtonId = "scrollToTopBtn";

    /** Scrolls the page to the top (respects reduced-motion) */
    export function scrollToTop(topLocatedAtInPixels: number = 0): void {
        const prefersReduced = globalThis.matchMedia(
            "(prefers-reduced-motion: reduce)",
        ).matches;
        const hasNativeSmooth =
            "scrollBehavior" in document.documentElement.style;

        if (hasNativeSmooth) {
            globalThis.scrollTo({
                top: topLocatedAtInPixels,
                behavior: prefersReduced ? "auto" : "smooth",
            });
        } else if ($) {
            $("html, body").animate(
                { scrollTop: topLocatedAtInPixels },
                prefersReduced ? 0 : 300,
            );
        } else {
            globalThis.scrollTo(0, topLocatedAtInPixels);
        }
    }
    // #endregion

    export function onLoad(): void {
        const scrollToTopButton = $("#" + ScrollToTopButtonId);

        $(window).on("scroll", function () {
            const jqueryWindowScrollTop = $(this).scrollTop();

            if (jqueryWindowScrollTop && jqueryWindowScrollTop > 150) {
                scrollToTopButton.fadeIn();
            } else {
                scrollToTopButton.fadeOut();
            }
        });

        scrollToTopButton.on("click", function (e) {
            e.preventDefault();
            scrollToTop();
        });

        globalThis.addEventListener("popstate", (event) => {
            notifyUrlChanged();
        });
    }
}

$(SaNavigationUtils.onLoad);

globalThis.SaNavigationUtils = SaNavigationUtils;
